import { z } from "zod";

const jsonObjectSchema = z.record(z.string(), z.unknown());

const merchantAgencyWireSchema = z
  .object({
    agency_name: z.string().min(1),
    agency_organization_id: z.string().min(1),
    agency_type: z.literal("agency"),
    created_at: z.string().datetime({ offset: true }),
    customer_organization_id: z.string().min(1),
    id: z.string().min(1),
    status: z.literal("active"),
    updated_at: z.string().datetime({ offset: true }),
  })
  .strict();

const merchantContextResourceWireSchema = z
  .object({
    connection_display_name: z.string().optional(),
    connection_id: z.string().optional(),
    connection_metadata_summary: jsonObjectSchema.optional(),
    display_name: z.string(),
    external_ad_account_id: z.string(),
    external_id: z.string(),
    external_shop_id: z.string(),
    external_subject: z.string(),
    id: z.string().min(1),
    metadata_summary: jsonObjectSchema,
    organization_id: z.string().min(1),
    provider: z.string().min(1),
    region: z.string(),
    resource_id: z.string().min(1),
    resource_type: z.string().min(1),
    status: z.string().min(1),
  })
  .strict();

const merchantContextProviderWireSchema = z
  .object({
    active_count: z.number().int().nonnegative(),
    provider: z.string().min(1),
    resource_type: z.string().min(1),
    selected_display_name: z.string().optional(),
    selected_resource_id: z.string().optional(),
    selected_source: z.string(),
    status: z.enum(["ambiguous", "missing", "selected", "singleton"]),
  })
  .strict();

const merchantContextSelectedWireSchema = z
  .object({
    reason: z.string(),
    resource: merchantContextResourceWireSchema,
    scope_type: z.literal("conversation"),
  })
  .strict();

const merchantContextSummaryWireSchema = z
  .object({
    agencies: z.array(merchantAgencyWireSchema),
    ambiguous: z.array(jsonObjectSchema),
    conversation_id: z.string(),
    membership: jsonObjectSchema.nullable(),
    missing: z.array(jsonObjectSchema),
    next_step: z.string(),
    organization: z
      .object({
        id: z.string().min(1),
        name: z.string(),
        status: z.string(),
        type: z.string(),
      })
      .strict(),
    organization_id: z.string().min(1),
    providers: z.array(merchantContextProviderWireSchema),
    resources: z.record(z.string(), z.array(merchantContextResourceWireSchema)),
    selected: z.record(z.string(), merchantContextSelectedWireSchema),
    summary_text: z.string(),
    user_id: z.string(),
  })
  .strict()
  .superRefine((response, context) => {
    if (response.organization.id !== response.organization_id) {
      context.addIssue({ code: "custom", message: "商家组织范围不一致" });
    }
    if (response.agencies.some((agency) => agency.customer_organization_id !== response.organization_id)) {
      context.addIssue({ code: "custom", message: "代理商关系超出当前商家范围" });
    }
  });

const publicSelectionWireSchema = z
  .object({
    conversation_id: z.string().nullable(),
    created_at: z.unknown(),
    display_name: z.string(),
    id: z.string().min(1),
    metadata_summary: jsonObjectSchema,
    organization_id: z.string().min(1),
    provider: z.string().min(1),
    reason: z.string(),
    resource_id: z.string().min(1),
    resource_type: z.literal("shop"),
    scope_type: z.literal("conversation"),
    selected_by_user_id: z.string().nullable(),
    source: z.enum(["explicit_user", "ui"]),
    status: z.string(),
    updated_at: z.unknown(),
    user_id: z.string().nullable(),
  })
  .strict();

export const merchantContextSummarySchema = merchantContextSummaryWireSchema.transform(
  (response) => {
    const resources = Object.values(response.resources).flat().map((resource) => ({
      connectionDisplayName: resource.connection_display_name ?? "",
      connectionId: resource.connection_id ?? "",
      displayName: resource.display_name,
      externalId: resource.external_id,
      metadataSummary: resource.metadata_summary,
      organizationId: resource.organization_id,
      provider: resource.provider,
      region: resource.region,
      resourceId: resource.resource_id,
      resourceType: resource.resource_type,
      status: resource.status,
    }));
    const shopResources = resources.filter((resource) => resource.resourceType === "shop");
    const explicitShop = Object.values(response.selected)
      .map((selection) => selection.resource)
      .find((resource) => resource.resource_type === "shop");
    const selectedShopWire = explicitShop ?? (shopResources.length === 1
      ? Object.values(response.resources).flat().find((resource) => resource.resource_type === "shop")
      : undefined);
    const selectionStatus: "selected" | "singleton" | "ambiguous" | "missing" = selectedShopWire
      ? explicitShop ? "selected" : "singleton"
      : shopResources.length > 1 ? "ambiguous" : "missing";

    return {
      agencies: response.agencies.map((agency) => ({
        name: agency.agency_name,
        organizationId: agency.agency_organization_id,
      })),
      conversationId: response.conversation_id,
      organizationId: response.organization_id,
      organizationName: response.organization.name,
      organizationType: response.organization.type,
      providers: response.providers.map((provider) => ({
        activeCount: provider.active_count,
        provider: provider.provider,
        resourceType: provider.resource_type,
        selectedResourceId: provider.selected_resource_id ?? "",
        status: provider.status,
      })),
      selectionStatus,
      shopResources,
      selectedShop: selectedShopWire
        ? {
            displayName: selectedShopWire.display_name,
            externalId: selectedShopWire.external_id,
            provider: selectedShopWire.provider,
            resourceId: selectedShopWire.resource_id,
          }
        : null,
      summaryText: response.summary_text,
      userId: response.user_id,
    };
  },
);

export const merchantContextSelectionResponseSchema = z
  .object({ selection: publicSelectionWireSchema })
  .strict()
  .transform((response) => ({
    selection: {
      displayName: response.selection.display_name,
      provider: response.selection.provider,
      resourceId: response.selection.resource_id,
    },
  }));

export type MerchantContextSummary = ReturnType<typeof merchantContextSummarySchema.parse>;
export type MerchantContextShopResource = MerchantContextSummary["shopResources"][number];
